import React, { useContext, useEffect, useMemo } from 'react';
import Dropdown from '@Components/Dropdown';
import SbTextField from '@Components/Textfield';
import {
  Checkbox,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useSnackbar } from 'notistack';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { SHIPMENT_MODES, SHIPMENT_UNITS, INCOTERM_OPTIONS, defaultBoxState } from '../../CreateShipment/constants';
import { SHIPMENT_TYPE } from '@Constants/Shipment';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { setInsureShipment, updateShipment } from '@Reducers/AdminShipment';
import { getSingleShipmentPackagePayload, getBoxStateObj, getUserDetailsStateObj } from '@Services/getShipmentPayload';
import { ShipmentControl } from '@Services/shipmentActionControl';
import CreateShipmentContext from '../../../context/CreateShipment';
import CreateShipmentWrapper from '../../../context/CreateShipment/Provider';
import { LengthType, createShipmentContextType } from '../../../types/shipment';
import { isActionAllowed } from '../../../utils/allowedActions';
import { isEUCountry, isComplianceRequired } from '../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../utils/currencyConversion';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isUSDestination, validateCpscCompliance } from '../../../utils/usCompliance';
import ShipmentDetailsBoxWise from '../../CreateShipment/ShipmentDetails';
import useScroll from '../useScroll';
import Actions from './Actions';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';

const getCountryName = (field: any): string => {
  if (!field) return '';
  if (typeof field === 'string') return field.trim();
  if (typeof field === 'object') {
    if (typeof field.country === 'string') return field.country.trim();
    if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
      return field.country.name.trim();
    }
    if (typeof field.name === 'string') return field.name.trim();
  }
  return '';
};

const SHIPMENT_TYPE_LITS = [
  { value: 'parcel', display: 'Parcel' },
  { value: 'document', display: 'Document' },
];

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

type StatusDetail = {
  status: string;
  [key: string]: any;
};

type Shipment = {
  statusDetails: StatusDetail[];
  [key: string]: any;
};

function isInTransit(shipment?: Shipment): boolean {
  if (!shipment || !shipment.statusDetails) return false;
  return shipment.statusDetails.some(detail => detail.status === 'in_transit');
}

function ShipmentDetails() {
  const dispatch = useDispatch<AppDispatch>();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const { name = '' } = organizationData || {};
  const { mutlipleShipmentsData } = useSelector((state: RootStateType) => state.trackShipment) || [];
  const checkForIsInTransit = isInTransit(mutlipleShipmentsData?.[0]);

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.write,
  );

  const isInTransitEditAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.edit_package_after_in_transit,
    PERFORM_ACTION.write,
  );

  const { shipmentDetailsRef } = useScroll();
  const {
    boxes,
    setBoxes,
    setRemoveFirstBoxDeleteOption,
    packageData,
    shipmentType,
    pickupType,
    setShipmentType,
    setPackageData,
    boxValidation,
    // selectedBox,
    setSelectedBox,
    setSelectedProduct,
    extraDetails,
    setExtraDetails,
    setConsignee,
  } = useContext(CreateShipmentContext) as createShipmentContextType;
  const { enqueueSnackbar } = useSnackbar();

  const handleShipmentModeChange = (newMode: string) => {
    const isNewB2B = newMode === 'B2B';
    const isNewB2C = newMode === 'B2C';

    const updatedShipment = { ...shipmentType, shipmentMode: newMode };
    const updatedExtra = { ...extraDetails };

    if (!isNewB2B) {
      updatedShipment.recipientVatTaxId = '';
      updatedExtra.recipientVatTaxId = '';
    }
    if (!isNewB2C) {
      updatedShipment.iossNumber = '';
      updatedExtra.iossNumber = '';
    }

    setShipmentType(updatedShipment);
    setPackageData({
      ...packageData,
      recipientVatTaxId: updatedShipment.recipientVatTaxId,
      iossNumber: updatedShipment.iossNumber,
    });
    setExtraDetails(updatedExtra);
  };

  const handleIncotermChange = (newIncoterm: string) => {
    const updatedShipment = { ...shipmentType, incoterm: newIncoterm };
    const updatedExtra = { ...extraDetails, incoterm: newIncoterm };

    if (newIncoterm === 'FOB') {
      updatedShipment.freight = 0;
      updatedShipment.freightInsurance = 0;
      updatedExtra.freight = 0;
      updatedExtra.freightInsurance = 0;
    } else if (newIncoterm === 'CNF') {
      updatedShipment.freightInsurance = 0;
      updatedExtra.freightInsurance = 0;
    }

    setShipmentType(updatedShipment);
    setPackageData({
      ...packageData,
      incoterm: newIncoterm,
      freight: updatedShipment.freight,
      freightInsurance: updatedShipment.freightInsurance,
    });
    setExtraDetails(updatedExtra);
  };

  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const shipmentActionControl = new ShipmentControl(shipmentById);

  useEffect(() => {
    if (shipmentById) {
      const { _packageId, shipmentMode, extraDetails, userReferenceNumber } = shipmentById;
      const { box, insurance, packaging, isDocument, unitOfMeasurement, currency, invoiceNumber } = _packageId;
      const boxData = getBoxStateObj(box || []);
      setBoxes(boxData);
      setRemoveFirstBoxDeleteOption(true);

      const anyData = shipmentById as any;
      const incoterm = anyData?.incoterm || _packageId?.incoterm || extraDetails?.other?.incoterm || 'FOB';
      const freight = anyData?.freight ?? _packageId?.freight ?? extraDetails?.other?.freight ?? 0;
      const freightInsurance =
        anyData?.freightInsurance ?? _packageId?.freightInsurance ?? extraDetails?.other?.freightInsurance ?? 0;
      const iossNumber = anyData?.iossNumber || _packageId?.iossNumber || extraDetails?.other?.iossNumber || '';
      const recipientVatTaxId =
        shipmentById?._consigneeId?.vat ||
        (shipmentById as any)?.consignee?.vat ||
        anyData?.consignee?.vat ||
        anyData?._consigneeId?.vat ||
        anyData?.recipientVatTaxId ||
        (_packageId as any)?.recipientVatTaxId ||
        (extraDetails?.other as any)?.recipientVatTaxId ||
        '';
      const iorEoriNumber =
        anyData?.iorEoriNumber ||
        _packageId?.iorEoriNumber ||
        extraDetails?.other?.ior ||
        extraDetails?.other?.iorEoriNumber ||
        '';

      setShipmentType({
        ...shipmentType,
        type: isDocument ? 'document' : 'parcel',
        shipmentMode,
        currency,
        unit: unitOfMeasurement === 'kg-cm' ? 'kg-cm' : 'lb-in',
        invoiceNumber: invoiceNumber || '',
        referenceNumber: userReferenceNumber,
        dutyCharge: shipmentById.dutyCharge || '',
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
      });
      setPackageData({
        ...packageData,
        unitOfMeasurement,
        insurance,
        packaging,
        userReferenceNumber: userReferenceNumber,
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
      });
      if (boxData.length) {
        setSelectedBox(boxData[0]);
      } else {
        setSelectedBox({ ...defaultBoxState });
      }
      setSelectedProduct(null);
      if (shipmentById?._consigneeId && setConsignee) {
        setConsignee(getUserDetailsStateObj(shipmentById._consigneeId));
      }
      if (extraDetails?.other) {
        setExtraDetails({
          ...extraDetails.other,
          incoterm,
          freight,
          freightInsurance,
          iossNumber,
          recipientVatTaxId,
          iorEoriNumber,
          ior: iorEoriNumber || extraDetails.other.ior || '',
        });
      }
    }
  }, [shipmentById]);

  const { currencyList } = useSelector((state: RootStateType) => state?.global);

  const destCountry = getCountryName(shipmentById?._consigneeId);
  const isDestinationEU = useMemo(() => isEUCountry(destCountry), [destCountry]);
  const { rate: eurRate } = useEurExchangeRate({ currency: shipmentType?.currency, isEU: isDestinationEU });
  const isB2C = (shipmentType.shipmentMode || '').toUpperCase() === 'B2C';
  const currentIncoterm = shipmentType.incoterm || 'FOB';
  const isCIF = currentIncoterm === 'CIF';
  const isCNF = currentIncoterm === 'CNF';
  const hasFreightInsuranceSection = isCIF || isCNF;
  const numericFreight = Number(shipmentType.freight || 0);
  const numericInsurance = Number(shipmentType.freightInsurance || 0);

  const resolvedShipmentValue = useMemo(() => {
    let sum = 0;
    (boxes || []).forEach(b => {
      (b.products || []).forEach((p: any) => {
        const q = Number(p.quantity ?? p.qty ?? 0);
        const v = Number(p.unitValue ?? p.value ?? 0);
        if (q > 0 && v > 0) sum += q * v;
      });
    });
    return Number(sum.toFixed(2));
  }, [boxes]);

  const eurEquivalentText = useMemo(() => {
    if (!isDestinationEU || !eurRate || (shipmentType?.currency || '').toUpperCase() === 'EUR') return '';
    const eurVal = (resolvedShipmentValue * eurRate).toFixed(2);
    return `(≈ €${eurVal})`;
  }, [isDestinationEU, eurRate, shipmentType?.currency, resolvedShipmentValue]);

  const totalCombinedValue = resolvedShipmentValue + numericFreight + numericInsurance;

  const onSave = () => {
    const isEcom = (shipmentType.shipmentMode || '').toUpperCase() === 'ECOM';
    const purposeVal = (extraDetails?.purposeOfShipment || '').trim();
    if (isEcom && !purposeVal) {
      enqueueSnackbar('Purpose Of Shipment is required for ECOM shipments', { variant: 'warning' });
      return;
    }

    const isUS = isUSDestination(destCountry);
    const complianceRequired = isComplianceRequired();
    if (isUS && complianceRequired) {
      let missingCpscMsg = '';
      for (let bIdx = 0; bIdx < (boxes || []).length; bIdx++) {
        const box = boxes[bIdx];
        for (let pIdx = 0; pIdx < (box.products || []).length; pIdx++) {
          const prod = box.products[pIdx];
          const cpscVal = validateCpscCompliance(prod.cpscCompliance, true, true);
          if (!cpscVal.isValid) {
            missingCpscMsg = `Box ${bIdx + 1}, Item ${pIdx + 1}: ${Object.values(cpscVal.errors)[0]}`;
            break;
          }
        }
        if (missingCpscMsg) break;
      }
      if (missingCpscMsg) {
        enqueueSnackbar(missingCpscMsg, { variant: 'warning' });
        return;
      }
    }

    const packageObj = getSingleShipmentPackagePayload({
      boxes,
      packageData: {
        ...packageData,
        incoterm: shipmentType.incoterm,
        freight: shipmentType.freight,
        freightInsurance: shipmentType.freightInsurance,
        iossNumber: shipmentType.iossNumber,
        iorEoriNumber: shipmentType.iorEoriNumber,
        ...(name === 'Cargo Solutions Network' && { isDropOff: pickupType.type === 'dropOff' }),
      },
      shipmentType: { ...shipmentType, unit: packageData.unitOfMeasurement as LengthType },
      pickupType,
    });

    const isAllowedDutySubdomain = isDutyChargesAllowedSubdomain();
    const currentType = getDutyChargeType(shipmentType.dutyCharge);
    const currentCharge = typeof shipmentType.dutyCharge === 'object' ? shipmentType.dutyCharge.charge || 0 : 0;
    const formattedDutyCharge =
      isAllowedDutySubdomain && currentType
        ? {
            type: currentType,
            charge: currentCharge,
          }
        : undefined;

    if (packageObj && isAllowedDutySubdomain && formattedDutyCharge) {
      packageObj.dutyCharge = formattedDutyCharge;
    }

    let idToUse;
    let isAccountHolder;
    if (shipmentById) {
      idToUse = shipmentById._accountId?.isAccountHolder ? shipmentById._accountId?._id : shipmentById?._userId?._id;
      isAccountHolder = shipmentById._accountId?.isAccountHolder;
    }

    if (shipmentById?.shipmentNumber && boxValidation) {
      const updatedExtraOther = {
        ...extraDetails,
        incoterm: shipmentType.incoterm || 'FOB',
        freight: Number(shipmentType.freight || 0),
        freightInsurance: Number(shipmentType.freightInsurance || 0),
        iossNumber: shipmentType.iossNumber || '',
        iorEoriNumber: shipmentType.iorEoriNumber || extraDetails?.ior || '',
        ior: shipmentType.iorEoriNumber || extraDetails?.ior || '',
      };

      dispatch(
        updateShipment({
          shipmentNumber: shipmentById.shipmentNumber,
          userReferenceNumber: shipmentType.referenceNumber,
          shipmentMode: shipmentType.shipmentMode,
          package: packageObj,
          extraDetails: { other: updatedExtraOther },
          accountId: idToUse,
          isAccountHolder: isAccountHolder,
          ...(isAllowedDutySubdomain &&
            formattedDutyCharge && {
              dutyCharge: formattedDutyCharge,
            }),
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(setInsureShipment(packageData.insurance));
  }, [packageData.insurance]);

  const theme = useTheme();

  return (
    <Box ref={shipmentDetailsRef} sx={{ mb: 3, width: '100%', maxWidth: '100%' }}>
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TuneOutlinedIcon />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                Shipment Specifications
              </Typography>
              <Chip
                label={shipmentType.type === 'document' ? 'Document' : 'Parcel'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
              Define shipment type, measurement units, and invoice declarations
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', boxSizing: 'border-box' }}>
          <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="ShipmentType"
                required
                label="Shipment Type"
                name="name"
                options={SHIPMENT_TYPE_LITS}
                value={shipmentType.type ? [shipmentType.type] : []}
                onChange={([value]) => setShipmentType({ ...shipmentType, type: value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            {isDestinationEU && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 18 }} />
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                    Goods value {shipmentType.currency || 'INR'} {resolvedShipmentValue.toFixed(2)}
                    {eurEquivalentText ? ` ${eurEquivalentText}` : ''} · €3 duty per line
                  </Typography>
                </Box>
              </Grid>
            )}
            {isDutyChargesAllowedSubdomain() && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 0.75 }}>
                    Duty & Tax Billed To
                  </Typography>
                  {(() => {
                    const currentDuty = getDutyChargeType(shipmentType.dutyCharge) || 'DDU';
                    const currentCharge =
                      typeof shipmentType.dutyCharge === 'object' ? shipmentType.dutyCharge.charge || 0 : 0;
                    return (
                      <Box>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            p: 0.5,
                            bgcolor: alpha(theme.palette.divider, 0.08),
                            borderRadius: '8px',
                            gap: 1,
                          }}
                        >
                          <Button
                            variant={currentDuty === 'DDU' ? 'contained' : 'text'}
                            size="small"
                            disabled={!isShipmentDetailsWriteAllowed}
                            onClick={() =>
                              setShipmentType({
                                ...shipmentType,
                                dutyCharge: {
                                  type: 'DDU',
                                  charge: currentCharge,
                                },
                              })
                            }
                            sx={{
                              px: 2.5,
                              py: 0.5,
                              borderRadius: '6px',
                              fontWeight: 700,
                              fontSize: '0.8125rem',
                              bgcolor: currentDuty === 'DDU' ? 'primary.main' : 'transparent',
                              color: currentDuty === 'DDU' ? 'common.white' : 'text.primary',
                            }}
                          >
                            DDU
                          </Button>
                          <Button
                            variant={currentDuty === 'DDP' ? 'contained' : 'text'}
                            size="small"
                            disabled={!isShipmentDetailsWriteAllowed}
                            onClick={() =>
                              setShipmentType({
                                ...shipmentType,
                                dutyCharge: {
                                  type: 'DDP',
                                  charge: currentCharge,
                                },
                              })
                            }
                            sx={{
                              px: 2.5,
                              py: 0.5,
                              borderRadius: '6px',
                              fontWeight: 700,
                              fontSize: '0.8125rem',
                              bgcolor: currentDuty === 'DDP' ? 'primary.main' : 'transparent',
                              color: currentDuty === 'DDP' ? 'common.white' : 'text.primary',
                            }}
                          >
                            DDP
                          </Button>
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                          {currentDuty === 'DDP' ? 'Added to your charges' : 'Consignee pays on delivery'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="ShipmentMode"
                required
                label="Shipment Mode"
                name="shipmentMode"
                options={SHIPMENT_MODES}
                value={shipmentType.shipmentMode ? [shipmentType.shipmentMode] : []}
                onChange={([value]) => handleShipmentModeChange(value)}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="preferredUnit"
                required
                label="Preferred Unit"
                name="name"
                options={SHIPMENT_UNITS}
                value={packageData.unitOfMeasurement ? [packageData.unitOfMeasurement] : []}
                onChange={([value]) => setPackageData({ ...packageData, unitOfMeasurement: value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="Currency"
                required
                label="Currency"
                name="name"
                options={currencyList}
                value={shipmentType.currency ? [shipmentType.currency] : []}
                onChange={([value]) => setShipmentType({ ...shipmentType, currency: value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="No Of Boxes"
                name="name"
                value={shipmentById?._packageId?.box?.length || ''}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="Reference No."
                name="name"
                value={shipmentType?.referenceNumber}
                onChange={e => setShipmentType({ ...shipmentType, referenceNumber: e.target.value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="Invoice No."
                name="name"
                disabled={!isShipmentDetailsWriteAllowed}
                value={shipmentType?.invoiceNumber || ''}
                onChange={e => setShipmentType({ ...shipmentType, invoiceNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="purposeOfShipment"
                required={(shipmentType.shipmentMode || '').toUpperCase() === 'ECOM'}
                label={`Purpose Of Shipment${(shipmentType.shipmentMode || '').toUpperCase() === 'ECOM' ? ' *' : ''}`}
                name="name"
                options={SHIPMENT_TYPE}
                value={extraDetails.purposeOfShipment ? [extraDetails.purposeOfShipment] : []}
                onChange={([value]) => setExtraDetails({ ...extraDetails, purposeOfShipment: value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="Incoterm"
                required
                label="Incoterm"
                name="incoterm"
                options={INCOTERM_OPTIONS}
                value={shipmentType.incoterm ? [shipmentType.incoterm] : ['FOB']}
                onChange={([value]) => handleIncotermChange(value)}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            {isB2C && isDestinationEU && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SbTextField
                  label="IOSS Number"
                  name="iossNumber"
                  placeholder="Enter IOSS Number"
                  value={shipmentType?.iossNumber || extraDetails?.iossNumber || ''}
                  onChange={e => {
                    setShipmentType({ ...shipmentType, iossNumber: e.target.value });
                    setPackageData({ ...packageData, iossNumber: e.target.value });
                    setExtraDetails({ ...extraDetails, iossNumber: e.target.value });
                  }}
                  disabled={!isShipmentDetailsWriteAllowed}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="IOR/EORI No."
                name="iorEoriNumber"
                placeholder="Enter IOR / EORI No."
                value={shipmentType?.iorEoriNumber || extraDetails?.ior || ''}
                onChange={value => {
                  setShipmentType({ ...shipmentType, iorEoriNumber: value.target.value });
                  setPackageData({ ...packageData, iorEoriNumber: value.target.value });
                  setExtraDetails({
                    ...extraDetails,
                    ior: value.target.value,
                    iorEoriNumber: value.target.value,
                  });
                }}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
          </Grid>

          {/* Freight & Insurance Section (Conditional on CIF / CNF) */}
          {hasFreightInsuranceSection && (
            <Box
              sx={{
                mt: 3,
                p: { xs: 2, sm: 2.5 },
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PaymentsOutlinedIcon color="primary" fontSize="small" />
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Freight & Insurance ({currentIncoterm})
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '14px', display: 'block', mb: 2 }}>
                {isCIF
                  ? 'CIF Incoterm includes both Freight and Insurance charges in the declaration.'
                  : 'CNF Incoterm includes Freight charges; Insurance is optional.'}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <SbTextField
                    required
                    type="number"
                    label={`Freight (${shipmentType.currency || 'INR'})`}
                    placeholder="Enter freight amount"
                    name="freight"
                    value={shipmentType.freight !== undefined ? shipmentType.freight : ''}
                    onChange={e => {
                      setShipmentType({ ...shipmentType, freight: e.target.value });
                      setPackageData({ ...packageData, freight: e.target.value });
                      setExtraDetails({ ...extraDetails, freight: e.target.value });
                    }}
                    disabled={!isShipmentDetailsWriteAllowed}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <SbTextField
                    required={isCIF}
                    type="number"
                    label={`Insurance (${shipmentType.currency || 'INR'})${isCIF ? '' : ' (Optional)'}`}
                    placeholder="Enter insurance amount"
                    name="freightInsurance"
                    value={shipmentType.freightInsurance !== undefined ? shipmentType.freightInsurance : ''}
                    onChange={e => {
                      setShipmentType({ ...shipmentType, freightInsurance: e.target.value });
                      setPackageData({ ...packageData, freightInsurance: e.target.value });
                      setExtraDetails({ ...extraDetails, freightInsurance: e.target.value });
                    }}
                    disabled={!isShipmentDetailsWriteAllowed}
                  />
                </Grid>
              </Grid>

              {/* Price Breakup Sub-section */}
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                  Price Breakup
                </Typography>
                <Divider sx={{ mb: 2, borderColor: alpha(theme.palette.divider, 0.08) }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                      Shipment Value (Goods)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: '14px', color: 'text.primary', mt: 0.5 }}
                    >
                      {shipmentType.currency || 'INR'} {resolvedShipmentValue.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                      Freight Amount
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: numericFreight > 0 ? 'text.primary' : 'text.secondary',
                        mt: 0.5,
                      }}
                    >
                      {shipmentType.currency || 'INR'} {numericFreight.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                      Insurance Amount
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: numericInsurance > 0 ? 'text.primary' : 'text.secondary',
                        mt: 0.5,
                      }}
                    >
                      {shipmentType.currency || 'INR'} {numericInsurance.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'primary.main', display: 'block' }}>
                      Total Declared Value
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                      {shipmentType.currency || 'INR'} {totalCombinedValue.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
          {name !== 'Cargo Solutions Network' && (
            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`, width: '100%' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 1.5 }}>
                Value-Added Services
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: { xs: 2, sm: 3, md: 4 },
                  width: '100%',
                }}
              >
                <Insurance
                  value={packageData.insurance}
                  onChange={value => setPackageData({ ...packageData, insurance: value })}
                />
                <Packaging
                  value={packageData.packaging}
                  onChange={value => setPackageData({ ...packageData, packaging: value })}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <ShipmentDetailsBoxWise
        removeShipmentInfo
        showViewMoreAction
        isShipmentDetailsWriteAllowed={isShipmentDetailsWriteAllowed}
      />
      {shipmentActionControl.canUpdateShipmentDetails && (
        <Actions
          disabled={!isShipmentDetailsWriteAllowed || (checkForIsInTransit && !isInTransitEditAllowed)}
          onSave={onSave}
        />
      )}
    </Box>
  );
}

// const IconStyles = { color: 'var(--mui-palette-primary-main)', height: '24px', width: '24px' };
interface InsuranceProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
function Insurance(props: InsuranceProps) {
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.write,
  );

  const dispatch = useDispatch();

  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disable = useMemo(() => !shipmentSetting?.insuranceCharge?.charge, [shipmentSetting?.insuranceCharge?.charge]);
  return (
    <Box
      className={`${disable ? 'disabled-form' : 'cursor-pointer'}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        cursor: disable ? 'not-allowed' : 'pointer',
        opacity: disable ? 0.6 : 1,
      }}
    >
      <Checkbox
        name="insurance"
        id="insurance"
        sx={{ mr: 0.5, p: 0.5 }}
        checked={!!props.value}
        onChange={() => {
          props.onChange(!props.value);
          dispatch(setInsureShipment(!props.value));
        }}
        inputProps={{ 'aria-label': 'controlled' }}
        disabled={disable || !isShipmentDetailsWriteAllowed}
      />
      <img src="/images/Boxes/Insurance.svg" alt="logistics" style={{ width: 22, height: 22, marginRight: 6 }} />
      <Typography
        component="label"
        htmlFor="insurance"
        sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', cursor: 'pointer', userSelect: 'none' }}
      >
        Insure Your Shipment
      </Typography>
    </Box>
  );
}

interface PackagingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}

function Packaging(props: PackagingProps) {
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.write,
  );

  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disable = useMemo(() => !shipmentSetting?.packagingCharge?.charge, [shipmentSetting?.packagingCharge?.charge]);
  return (
    <Box
      className={`${disable ? 'disabled-form' : 'cursor-pointer'}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        cursor: disable ? 'not-allowed' : 'pointer',
        opacity: disable ? 0.6 : 1,
      }}
    >
      <Checkbox
        name="packaging"
        id="packaging"
        sx={{ mr: 0.5, p: 0.5 }}
        checked={!!props.value}
        onChange={() => props.onChange(!props.value)}
        inputProps={{ 'aria-label': 'controlled' }}
        disabled={disable || !isShipmentDetailsWriteAllowed}
      />
      <img src="/images/Boxes/Box.svg" alt="logistics" style={{ width: 22, height: 22, marginRight: 6 }} />
      <Typography
        component="label"
        htmlFor="packaging"
        sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', cursor: 'pointer', userSelect: 'none' }}
      >
        Add Packaging
      </Typography>
    </Box>
  );
}

function ContextWrapper() {
  return (
    <CreateShipmentWrapper>
      <ShipmentDetails />
    </CreateShipmentWrapper>
  );
}

export default ContextWrapper;
